import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../../../lib/api'
import { leadService } from '../../leads/services/leads'
import { telephonyService } from '../../telephony/services/telephony'
import type { Lead, Reminder } from '../../../types'
import { UserRole, CallStatus, TERMINAL_CALL_STATUSES, LeadStatus } from '../../../types'
import { useCurrentUser } from '../../../hooks/useCurrentUser'
import CallDispositionModal from '../../leads/components/CallDispositionModal'
import EnquiryTrackingCard from '../../leads/components/EnquiryTrackingCard'
import { LEAD_STATUS_COLORS, LEAD_STATUS_LABELS, priorityBadgeClass } from '../../../lib/constants'
import {
  Phone, Info, Clock, Bell, CheckCircle, AlertCircle, Plus, X, Percent,
  Eye, EyeOff, Search, SlidersHorizontal, PhoneCall, ChevronDown, ChevronUp,
} from 'lucide-react'
import { LeadSource, CallSource } from '../../../types'
import { ModalOverlay } from '../../../components/ModalOverlay'
import { useOfflineDisposition } from '../../../lib/useOfflineLeads'
import { isNativeApp } from '../../../lib/platform'
import { toLocalInputValue } from '../../../lib/datetime'
import { pollAndUpload } from '../../telephony/services/nativeRecording'

// ── Helpers ───────────────────────────────────────────────────────────────────

interface MyCallLog {
  id: number
  lead_id: number
  od_discount_offered: number | null
  approval_status: string | null
  escalated_to: string | null
}

type Tab = 'queue' | 'reminders'

type SortKey = 'priority' | 'due_date' | 'last_contacted'

const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  [LeadSource.NEW_VEHICLE_SALE]: 'New Vehicle',
  [LeadSource.RENEWAL]:         'Renewal',
  [LeadSource.BODY_SHOP]:       'Body Shop',
  [LeadSource.WALK_IN]:         'Walk-in',
  [LeadSource.OEM_DUMP]:        'OEM Dump',
  [LeadSource.API]:             'API',
  [LeadSource.BODY_MANAGEMENT]: 'Body Mgmt',
  [LeadSource.OTHER]:           'Other',
}

// The workspace queue only ever contains active (non-closed) leads.
const ACTIVE_LEAD_STATUSES: LeadStatus[] = [
  LeadStatus.NEW,
  LeadStatus.WHATSAPP_QUALIFYING,
  LeadStatus.QUALIFIED,
  LeadStatus.CALLBACK_REQUESTED,
  LeadStatus.QUOTATION_SENT,
  LeadStatus.IN_NEGOTIATION,
]

function daysRemaining(dateStr: string | null): number | null {
  if (!dateStr) return null
  const diff = new Date(dateStr).getTime() - Date.now()
  return Math.ceil(diff / 86_400_000)
}

function daysColor(days: number | null): string {
  if (days === null) return 'var(--text-tertiary)'
  if (days <= 7) return 'var(--danger-text)'
  if (days <= 30) return 'var(--warning-text)'
  return 'var(--success-text)'
}

function daysLabel(days: number | null): string | null {
  if (days === null) return null
  if (days === 0) return 'Due today'
  if (days < 0) return `${Math.abs(days)}d overdue`
  return `${days}d left`
}

const CALL_STATUS_POLL_INTERVAL = 5000
// If the call never reaches a terminal status within this window (backend
// webhook lost, provider outage, etc.), stop polling and let the telecaller
// log the disposition manually instead of leaving the modal stuck forever.
const CALL_STATUS_POLL_TIMEOUT = 3 * 60 * 1000
const PAGE_SIZE = 20

// ── Component ─────────────────────────────────────────────────────────────────

export default function TelecallerWorkspace() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const highlightLeadId = searchParams.get('lead_id') ? Number(searchParams.get('lead_id')) : null
  const leadCardRefs = useRef<Record<number, HTMLDivElement | null>>({})

  // "Show more" must only appear when content is actually clamped/overflowing —
  // measure the truncated reason (vertical) and WA summary (horizontal) spans.
  const clampRefs = useRef<Record<number, { reason: HTMLElement | null; wa: HTMLElement | null }>>({})
  const [overflowIds, setOverflowIds] = useState<Set<number>>(new Set())

  const [tab, setTab]             = useState<Tab>('queue')
  const [leads, setLeads]         = useState<Lead[]>([])
  const [reminders, setReminders] = useState<Reminder[]>([])
  const [loading, setLoading]     = useState(true)

  // Sort / filter / pagination state
  const [search, setSearch]               = useState('')
  const [sortKey, setSortKey]             = useState<SortKey>('priority')
  const [filterStatuses, setFilterStatuses] = useState<Set<LeadStatus>>(new Set())
  const [filterOpen, setFilterOpen]       = useState(false)
  const [page, setPage]                   = useState(1)

  // OD discount badges
  const [myCallLogs, setMyCallLogs] = useState<MyCallLog[]>([])

  // Per-card expand (shows truncated priority reason + WA summary in full)
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set())
  const toggleExpand = (id: number) =>
    setExpandedCards(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })

  const { submitDisposition } = useOfflineDisposition()

  const currentUser = useCurrentUser()
  const isTeamLeader = currentUser?.role === UserRole.TEAM_LEADER
  const smartConnectEnabled = !!currentUser?.tenant_feature_flags?.['smart_connect_enabled']
  const logImmediately = !!currentUser?.tenant_feature_flags?.['smart_connect_log_immediately']

  // Active call state
  const [initiating, setInitiating]         = useState<number | null>(null)
  const [activeCallLogId, setActiveCallLogId] = useState<number | null>(null)
  const [activeLead, setActiveLead]           = useState<Lead | null>(null)
  const [activeCallStatus, setActiveCallStatus] = useState<CallStatus | null>(null)
  const [activeCallSource, setActiveCallSource] = useState<CallSource>(CallSource.EXOTEL)
  const [showDisposition, setShowDisposition] = useState(false)
  const [callError, setCallError]             = useState('')
  const [slotWarning, setSlotWarning]         = useState('')
  const [pollTimedOut, setPollTimedOut]       = useState(false)
  const [recordingFound, setRecordingFound]   = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollStartedAtRef = useRef<number | null>(null)

  // Mobile reveal: maps lead.id → revealed number (auto-clears after 30s)
  const [revealedMobiles, setRevealedMobiles] = useState<Record<number, string>>({})
  const revealTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})

  // Reminder creation
  const [reminderLead, setReminderLead]   = useState<Lead | null>(null)
  const [reminderAt, setReminderAt]       = useState('')
  const [reminderNote, setReminderNote]   = useState('')
  const [savingReminder, setSavingReminder] = useState(false)
  const [reminderError, setReminderError] = useState('')

  // Native recording config (fetched once at mount)
  const [recordingMode, setRecordingMode] = useState('android_native')
  const [, setAndroidOem]                  = useState('samsung')

  // Enquiry tracking slide-over
  const [slideOverLeadId, setSlideOverLeadId] = useState<number | null>(null)
  const [slideOverName, setSlideOverName]     = useState('')

  // ── Data loading ─────────────────────────────────────────────────────────

  const fetchQueue = async () => {
    setLoading(true)
    try {
      const data = await leadService.getMyQueue()
      setLeads(data)
    } finally {
      setLoading(false)
    }
  }

  const fetchReminders = async () => {
    try {
      setReminders(await telephonyService.getMyReminders())
    } catch { /* non-critical */ }
  }

  const fetchMyCallLogs = async () => {
    try {
      const res = await api.get('/calls/my-logs')
      setMyCallLogs(res.data.filter((c: MyCallLog) => c.od_discount_offered && c.od_discount_offered > 0))
    } catch { /* non-critical */ }
  }

  useEffect(() => {
    fetchQueue()
    fetchReminders()
    fetchMyCallLogs()
    telephonyService.getRecordingConfig().then(cfg => {
      setRecordingMode(cfg.call_recording_mode)
      setAndroidOem(cfg.android_oem)
    }).catch(() => { /* use defaults */ })
  }, [])

  // Scroll to and highlight the lead when navigated here from a push notification or Dashboard
  useEffect(() => {
    if (!highlightLeadId || loading) return
    const el = leadCardRefs.current[highlightLeadId]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlightLeadId, loading])

  // ── Call status polling ───────────────────────────────────────────────────

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const startPolling = (callLogId: number) => {
    stopPolling()
    setPollTimedOut(false)
    pollStartedAtRef.current = Date.now()
    pollRef.current = setInterval(async () => {
      const startedAt = pollStartedAtRef.current
      if (startedAt !== null && Date.now() - startedAt >= CALL_STATUS_POLL_TIMEOUT) {
        stopPolling()
        setPollTimedOut(true)
        return
      }
      try {
        const status = await leadService.getCallStatus(callLogId)
        setActiveCallStatus(status.call_status)
        if (TERMINAL_CALL_STATUSES.has(status.call_status)) {
          stopPolling()
        }
      } catch { /* non-critical — keep polling */ }
    }, CALL_STATUS_POLL_INTERVAL)
  }

  useEffect(() => () => { stopPolling() }, [])

  // ── Call handlers ─────────────────────────────────────────────────────────

  const handleInitiateCall = async (lead: Lead) => {
    setCallError('')
    setSlotWarning('')
    setInitiating(lead.id)
    try {
      if (smartConnectEnabled) {
        // ── Exotel click-to-call (Smart Connect ON) ─────────────────────
        try {
          const result = await leadService.initiateCall(lead.id)
          if (result.within_requested_slot === false) {
            setSlotWarning(`You're calling ${lead.customer_name} outside their requested callback time.`)
          }
          setActiveCallLogId(result.call_log_id)
          setActiveLead(lead)
          setActiveCallSource(CallSource.EXOTEL)
          setActiveCallStatus(CallStatus.INITIATED)
          setShowDisposition(true)
          startPolling(result.call_log_id)
        } catch (exotelErr: unknown) {
          // ── Fallback: native dialer on mobile, error on desktop ──────────
          if (await isNativeApp()) {
            setCallError('Smart Connect unavailable — opening native dialer.')
            const { mobile } = await leadService.revealMobile(lead.id)
            const { call_log_id: mobileCallLogId } = await leadService.mobileInitiateCall(lead.id)
            const callStartedAt = new Date()
            window.open(`tel:${mobile}`)
            setActiveLead(lead)
            setActiveCallLogId(mobileCallLogId)
            setActiveCallSource(CallSource.MANUAL_MOBILE)
            setActiveCallStatus(null)
            setShowDisposition(true)
            if (recordingMode === 'android_native') {
              // pollAndUpload waits internally for inactive→active (call end) then polls
              pollAndUpload(mobileCallLogId, callStartedAt).then(pollResult => {
                setActiveCallStatus(CallStatus.COMPLETED)
                if (pollResult === 'uploaded') setRecordingFound(true)
              })
            } else {
              setActiveCallStatus(CallStatus.COMPLETED)
            }
          } else {
            const detail = (exotelErr as { response?: { data?: { detail?: string } } })?.response?.data?.detail
            setCallError(detail || 'Could not initiate call. Check Exotel is configured and your account has a mobile number set.')
          }
        }
      } else if (await isNativeApp()) {
        // ── Native dialer (Smart Connect OFF · Mobile) ───────────────────
        const { mobile } = await leadService.revealMobile(lead.id)
        const { call_log_id: mobileCallLogId } = await leadService.mobileInitiateCall(lead.id)
        const callStartedAt = new Date()
        window.open(`tel:${mobile}`)
        setActiveLead(lead)
        setActiveCallLogId(mobileCallLogId)
        setActiveCallSource(CallSource.MANUAL_MOBILE)
        setActiveCallStatus(null)
        setShowDisposition(true)
        if (recordingMode === 'android_native') {
          // pollAndUpload waits internally for inactive→active (call end) then polls
          pollAndUpload(mobileCallLogId, callStartedAt).then(pollResult => {
            setActiveCallStatus(CallStatus.COMPLETED)
            if (pollResult === 'uploaded') setRecordingFound(true)
          })
        } else {
          setActiveCallStatus(CallStatus.COMPLETED)
        }
      } else {
        // ── Desktop manual (Smart Connect OFF · Desktop) ─────────────────
        await handleRevealMobile(lead)
        if (logImmediately) {
          setActiveLead(lead)
          setActiveCallLogId(null)
          setActiveCallSource(CallSource.MANUAL_DESKTOP)
          setActiveCallStatus(null)
          setShowDisposition(true)
        }
        // Otherwise the "Log Call" button that appears after reveal will open the modal.
      }
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setCallError(detail || 'Could not initiate call.')
    } finally {
      setInitiating(null)
    }
  }

  const handleLogManualCall = (lead: Lead) => {
    setActiveLead(lead)
    setActiveCallLogId(null)
    setActiveCallSource(CallSource.MANUAL_DESKTOP)
    setActiveCallStatus(null)
    setShowDisposition(true)
  }

  const handleCallSaved = async () => {
    stopPolling()
    setShowDisposition(false)
    setActiveCallLogId(null)
    setActiveLead(null)
    setActiveCallStatus(null)
    setPollTimedOut(false)
    setRecordingFound(false)
    await fetchQueue()
    await fetchReminders()
  }

  const handleDispositionClose = () => {
    // Only allow close when no active terminal-pending state — keep modal open until terminal
    // (or until polling has given up after CALL_STATUS_POLL_TIMEOUT).
    if (pollTimedOut || activeCallStatus === null || TERMINAL_CALL_STATUSES.has(activeCallStatus)) {
      stopPolling()
      setShowDisposition(false)
      setActiveCallLogId(null)
      setActiveLead(null)
      setActiveCallStatus(null)
      setPollTimedOut(false)
      setRecordingFound(false)
    }
  }

  // ── Mobile reveal ─────────────────────────────────────────────────────────

  const handleRevealMobile = async (lead: Lead) => {
    try {
      const { mobile } = await leadService.revealMobile(lead.id)
      setRevealedMobiles(prev => ({ ...prev, [lead.id]: mobile }))
      // Auto-re-mask after 30 seconds
      if (revealTimers.current[lead.id]) clearTimeout(revealTimers.current[lead.id])
      revealTimers.current[lead.id] = setTimeout(() => {
        setRevealedMobiles(prev => {
          const next = { ...prev }
          delete next[lead.id]
          return next
        })
      }, 30_000)
    } catch { /* silently ignore — mobile stays masked */ }
  }

  const handleReMask = (leadId: number) => {
    if (revealTimers.current[leadId]) clearTimeout(revealTimers.current[leadId])
    setRevealedMobiles(prev => {
      const next = { ...prev }
      delete next[leadId]
      return next
    })
  }

  // Cleanup reveal timers on unmount
  useEffect(() => () => {
    Object.values(revealTimers.current).forEach(clearTimeout)
  }, [])

  // ── Reminder handlers ─────────────────────────────────────────────────────

  const handleCompleteReminder = async (reminderId: number) => {
    try {
      await telephonyService.completeReminder(reminderId)
      setReminders(prev => prev.filter(r => r.id !== reminderId))
    } catch {
      setCallError('Failed to complete reminder')
    }
  }

  const openReminderModal = (lead: Lead) => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000)
    d.setSeconds(0, 0)
    setReminderAt(toLocalInputValue(d))
    setReminderNote('')
    setReminderError('')
    setReminderLead(lead)
  }

  const handleSaveReminder = async () => {
    if (!reminderLead || !reminderAt) return
    setSavingReminder(true)
    setReminderError('')
    try {
      await telephonyService.createReminder({
        lead_id: reminderLead.id,
        reminder_at: new Date(reminderAt).toISOString(),
        note: reminderNote.trim() || undefined,
      })
      setReminderLead(null)
      await fetchReminders()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setReminderError(detail || 'Failed to save reminder')
    } finally {
      setSavingReminder(false)
    }
  }

  // ── Sort / filter logic ───────────────────────────────────────────────────

  const toggleStatusFilter = (status: LeadStatus) => {
    setFilterStatuses(prev => {
      const next = new Set(prev)
      if (next.has(status)) next.delete(status)
      else next.add(status)
      return next
    })
  }

  const displayedLeads = (() => {
    let result = [...leads]

    // Text search — customer name, vehicle reg, or lead status label
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      result = result.filter(l =>
        l.customer_name.toLowerCase().includes(q) ||
        (l.vehicle_reg_no?.toLowerCase().includes(q)) ||
        (LEAD_STATUS_LABELS[l.status]?.toLowerCase().includes(q)) ||
        l.status.toLowerCase().replace(/_/g, ' ').includes(q)
      )
    }

    if (filterStatuses.size > 0) {
      result = result.filter(l => filterStatuses.has(l.status))
    }

    result.sort((a, b) => {
      if (sortKey === 'due_date') {
        const da = a.insurance_due_date ? new Date(a.insurance_due_date).getTime() : Infinity
        const db_ = b.insurance_due_date ? new Date(b.insurance_due_date).getTime() : Infinity
        return da - db_
      }
      if (sortKey === 'last_contacted') {
        const la = a.last_contacted_at ? new Date(a.last_contacted_at).getTime() : 0
        const lb = b.last_contacted_at ? new Date(b.last_contacted_at).getTime() : 0
        return lb - la
      }
      // priority (default)
      return b.priority_score - a.priority_score
    })

    return result
  })()

  const pendingReminders = reminders.filter(r => !r.is_completed)

  const totalPages = Math.max(1, Math.ceil(displayedLeads.length / PAGE_SIZE))
  const paginatedLeads = displayedLeads.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // Recompute which cards genuinely overflow (so the toggle isn't a no-op).
  // Keyed on the rendered ids + expand state so it doesn't loop on every render.
  const paginatedKey = paginatedLeads.map(l => l.id).join(',')
  useLayoutEffect(() => {
    const measure = () => setOverflowIds(prev => {
      const next = new Set(prev)
      for (const lead of paginatedLeads) {
        // Clamp classes are removed while expanded — can't measure, keep prior state.
        if (expandedCards.has(lead.id)) continue
        const r = clampRefs.current[lead.id]
        const overflows = !!r && (
          (!!r.reason && r.reason.scrollHeight > r.reason.clientHeight + 1) ||
          (!!r.wa && r.wa.scrollWidth > r.wa.clientWidth + 1)
        )
        if (overflows) next.add(lead.id)
        else next.delete(lead.id)
      }
      if (next.size === prev.size && [...next].every(id => prev.has(id))) return prev
      return next
    })
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedKey, expandedCards])

  // Reset to page 1 when search / filter / sort changes
  useEffect(() => { setPage(1) }, [search, sortKey, filterStatuses])

  // Jump to the page containing the highlighted lead
  useEffect(() => {
    if (!highlightLeadId || loading) return
    const idx = displayedLeads.findIndex(l => l.id === highlightLeadId)
    if (idx >= 0) setPage(Math.floor(idx / PAGE_SIZE) + 1)
  }, [highlightLeadId, loading]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    // Fill the full main area height; only the list scrolls — header/tabs/filters stay fixed
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Fixed top: header + error + tabs + filter bar ─────────────────── */}
      <div className="shrink-0 space-y-3">

        {/* Header */}
        <div className="section-header" style={{ paddingBottom: 0 }}>
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Phone className="h-6 w-6" style={{ color: 'var(--brand-600)' }} />
              My Workspace
            </h1>
            <p className="page-subtitle">Prioritized lead queue · calls bridged via Exotel</p>
          </div>
        </div>

        {callError && (
          <div className="alert-danger">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{callError}</span>
            <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setCallError('')}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {slotWarning && (
          <div className="alert-warning">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{slotWarning}</span>
            <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setSlotWarning('')}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1" style={{ borderBottom: '1px solid var(--border-default)' }}>
          {([
            ['queue',     `Active Leads (${displayedLeads.length}${displayedLeads.length !== leads.length ? `/${leads.length}` : ''})`,   Phone, null],
            ['reminders', 'Reminders',                        Bell,  pendingReminders.length > 0 ? pendingReminders.length : null],
          ] as const).map(([key, label, Icon, badge]) => (
            <button
              key={key}
              className="px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 transition-colors"
              style={{
                borderBottomColor: tab === key ? 'var(--brand-primary)' : 'transparent',
                color: tab === key ? 'var(--brand-primary)' : 'var(--text-tertiary)',
              }}
              onClick={() => setTab(key)}
            >
              <Icon className="h-4 w-4" />
              {label}
              {badge && (
                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: 'var(--danger-text)', color: '#fff' }}>
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Sort / filter bar — only shown in queue tab */}
        {tab === 'queue' && (
          <div className="space-y-2">
            {/* Row 1: search + sort + filter toggle — always one line */}
            <div className="flex items-center gap-2">
              {/* Search — grows to fill available space */}
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none"
                  style={{ color: 'var(--text-disabled)' }} />
                <input
                  type="text"
                  className="input pl-8 text-sm w-full"
                  placeholder="Search…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              {/* Sort — compact; CSS already injects a chevron via background-image */}
              <div className="shrink-0">
                <select
                  className="input text-sm"
                  value={sortKey}
                  onChange={e => setSortKey(e.target.value as SortKey)}
                  aria-label="Sort by"
                >
                  <option value="priority">Priority</option>
                  <option value="due_date">Due Date</option>
                  <option value="last_contacted">Last Call</option>
                </select>
              </div>

              {/* Filter toggle button — count shown inline to avoid overflow-hidden clipping */}
              <button
                onClick={() => setFilterOpen(p => !p)}
                className="btn-icon shrink-0"
                aria-label="Toggle filters"
                title="Toggle filters"
                aria-expanded={filterOpen}
                style={{
                  width: filterStatuses.size > 0 ? 'auto' : undefined,
                  padding: filterStatuses.size > 0 ? '0 8px' : undefined,
                  gap: 4,
                  color: filterStatuses.size > 0 ? 'var(--brand-primary)' : 'var(--text-secondary)',
                  border: `1px solid ${filterStatuses.size > 0 ? 'var(--brand-primary)' : 'var(--border-default)'}`,
                  background: filterStatuses.size > 0 ? 'var(--brand-primary-subtle)' : 'transparent',
                }}
              >
                <SlidersHorizontal className="h-4 w-4" />
                {filterStatuses.size > 0 && (
                  <span
                    className="rounded-full flex items-center justify-center"
                    style={{
                      width: 16, height: 16, fontSize: 10, fontWeight: 700,
                      background: 'var(--brand-primary)',
                      color: '#fff',
                      flexShrink: 0,
                    }}
                  >
                    {filterStatuses.size}
                  </span>
                )}
              </button>
            </div>

            {/* Row 2: status filter chips — collapsible */}
            {filterOpen && (
              <div className="flex gap-1.5 flex-wrap">
                {ACTIVE_LEAD_STATUSES.map(status => (
                  <button
                    key={status}
                    onClick={() => toggleStatusFilter(status)}
                    className="text-xs px-2.5 py-1 rounded-full border transition-colors"
                    style={{
                      borderColor: filterStatuses.has(status) ? 'var(--brand-primary)' : 'var(--border-default)',
                      background: filterStatuses.has(status) ? 'var(--brand-primary-subtle)' : 'transparent',
                      color: filterStatuses.has(status) ? 'var(--brand-primary)' : 'var(--text-tertiary)',
                    }}
                  >
                    {LEAD_STATUS_LABELS[status]}
                  </button>
                ))}
                {filterStatuses.size > 0 && (
                  <button
                    onClick={() => setFilterStatuses(new Set())}
                    className="text-xs px-2.5 py-1 rounded-full border transition-colors"
                    style={{
                      borderColor: 'var(--danger-text)',
                      color: 'var(--danger-text)',
                      background: 'transparent',
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Scrollable list ────────────────────────────────────────────────── */}
      <div
        className="flex-1 min-h-0 overflow-y-auto"
        style={{ paddingTop: 12, paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
      >

      {/* ── Call Queue ────────────────────────────────────────────────────── */}
      {tab === 'queue' && (
        <div className="space-y-3">

          {loading ? (
            <div className="empty-state">Loading…</div>
          ) : displayedLeads.length === 0 ? (
            <div className="empty-state">
              <Phone className="h-10 w-10 mb-3" style={{ color: 'var(--text-disabled)' }} />
              <p>{leads.length === 0 ? 'No leads in your queue' : 'No leads match the filters'}</p>
            </div>
          ) : (
            paginatedLeads.map((lead) => {
              const discountLog = myCallLogs
                .filter(c => c.lead_id === lead.id)
                .sort((a, b) => b.id - a.id)[0]

              const discountBadge = (() => {
                if (!discountLog?.od_discount_offered) return null
                const pct    = discountLog.od_discount_offered
                const status = discountLog.approval_status?.toUpperCase() ?? null
                if (status === null) return null
                if (status === 'APPROVED')
                  return { label: `${pct}% ✓ Approved`, color: 'var(--success-text)', bg: 'var(--success-bg)' }
                if (status === 'REJECTED')
                  return { label: `${pct}% ✗ Rejected`, color: 'var(--danger-text)', bg: 'var(--danger-bg)' }
                if (status === 'PENDING') {
                  const who = discountLog.escalated_to === 'TEAM_LEADER' ? 'TL' : 'IH'
                  return { label: `${pct}% ⏳ Pending ${who}`, color: 'var(--warning-text)', bg: 'var(--warning-bg)' }
                }
                return null
              })()

              const days  = daysRemaining(lead.insurance_due_date)
              const dColor = daysColor(days)
              const dLabel = daysLabel(days)
              const revealedMobile = revealedMobiles[lead.id]
              const isCallActive = activeCallLogId !== null
              const customerSlot = reminders.find(
                r => r.lead_id === lead.id && r.source === 'CUSTOMER_REQUESTED' && !r.is_completed && r.slot_start
              )

              const isHighlighted = lead.id === highlightLeadId

              const isExpanded = expandedCards.has(lead.id)
              const hasTruncatable = overflowIds.has(lead.id)

              return (
                <div
                  key={lead.id}
                  ref={el => { leadCardRefs.current[lead.id] = el }}
                  className="card"
                  style={isHighlighted ? { outline: '2px solid var(--brand-primary)', outlineOffset: 2 } : undefined}
                >
                  {/* ── Row 1: name + ℹ 🔔 Call ── */}
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <h3
                      className="font-semibold truncate cursor-pointer hover:underline flex-1 min-w-0"
                      title={lead.customer_name}
                      style={{ color: 'var(--brand-600)' }}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                    >
                      {lead.customer_name}
                    </h3>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        className="btn-icon"
                        title="View lead history"
                        onClick={() => { setSlideOverLeadId(lead.id); setSlideOverName(lead.customer_name) }}
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                      <button
                        className="btn-icon"
                        title="Set reminder"
                        onClick={() => openReminderModal(lead)}
                      >
                        <Bell className="h-3.5 w-3.5" />
                      </button>
                      <button
                        className="btn-primary"
                        onClick={() => handleInitiateCall(lead)}
                        disabled={initiating === lead.id || isCallActive}
                        title={isCallActive && initiating !== lead.id ? 'Complete the current call first' : undefined}
                      >
                        <Phone className={`h-3.5 w-3.5 ${initiating === lead.id ? 'animate-pulse' : ''}`} />
                        {initiating === lead.id ? 'Calling…' : 'Call'}
                      </button>
                    </div>
                  </div>

                  {/* ── Row 2: badges ── */}
                  <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                    <span className={priorityBadgeClass(lead.priority_score)}>
                      Score: {lead.priority_score}
                    </span>
                    <span className={`${LEAD_STATUS_COLORS[lead.status]} text-xs`}>
                      {LEAD_STATUS_LABELS[lead.status]}
                    </span>
                    <span className="badge-neutral text-xs">
                      {LEAD_SOURCE_LABELS[lead.lead_source]}
                    </span>
                    {discountBadge && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{ color: discountBadge.color, background: discountBadge.bg }}>
                        <Percent className="h-3 w-3" />{discountBadge.label}
                      </span>
                    )}
                    {customerSlot && (
                      <span className="badge-info text-xs" title="Customer picked this callback time on WhatsApp">
                        <Clock className="h-3 w-3" style={{ display: 'inline', marginRight: 3 }} />
                        {new Date(customerSlot.slot_start!).toLocaleString(undefined, {
                          weekday: 'short', hour: 'numeric', minute: '2-digit',
                        })}
                      </span>
                    )}
                  </div>

                  {/* ── Row 3: priority reason (truncated unless expanded) ── */}
                  {lead.priority_reason && (
                    <p
                      className="text-xs flex items-start gap-1 mb-1.5"
                      style={{ color: 'var(--text-tertiary)' }}
                    >
                      <Info className="h-3 w-3 shrink-0 mt-px" />
                      <span
                        ref={el => { (clampRefs.current[lead.id] ??= { reason: null, wa: null }).reason = el }}
                        className={isExpanded ? '' : 'line-clamp-1'}
                      >
                        {lead.priority_reason}
                      </span>
                    </p>
                  )}

                  {/* ── Row 4: vehicle · reg  +  due date ── */}
                  <div className="flex items-center justify-between gap-2 text-xs mb-1.5"
                    style={{ color: 'var(--text-tertiary)' }}>
                    <span className="truncate">
                      {lead.vehicle_model && lead.vehicle_reg_no
                        ? `${lead.vehicle_model} · ${lead.vehicle_reg_no}`
                        : lead.vehicle_model || lead.vehicle_reg_no || '—'}
                    </span>
                    {lead.insurance_due_date && (
                      <span className="flex items-center gap-1 shrink-0">
                        <Clock className="h-3 w-3" />
                        {new Date(lead.insurance_due_date).toLocaleDateString()}
                        {dLabel && (
                          <span className="font-medium" style={{ color: dColor }}>({dLabel})</span>
                        )}
                      </span>
                    )}
                  </div>

                  {/* ── Row 5: mobile  +  WA summary ── */}
                  <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    {/* Mobile reveal */}
                    {isTeamLeader ? (
                      <span className="font-medium shrink-0" style={{ color: 'var(--text-primary)' }}>
                        {lead.mobile}
                      </span>
                    ) : revealedMobile ? (
                      <span className="flex items-center gap-1 shrink-0">
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                          {revealedMobile}
                        </span>
                        <button className="btn-icon" style={{ padding: '2px' }} title="Re-mask"
                          onClick={() => handleReMask(lead.id)}>
                          <EyeOff className="h-3 w-3" />
                        </button>
                      </span>
                    ) : (
                      <button
                        className="flex items-center gap-1 shrink-0 hover:underline"
                        style={{ color: 'var(--brand-primary)' }}
                        onClick={() => handleRevealMobile(lead)}
                      >
                        <Eye className="h-3 w-3" />
                        {lead.mobile}
                      </button>
                    )}

                    {/* WA summary */}
                    <span
                      className="flex items-center gap-1 min-w-0"
                      style={{ color: 'var(--text-tertiary)' }}
                    >
                      <span className="font-semibold shrink-0" style={{ color: 'var(--success-text)' }}>WA</span>
                      <span
                        ref={el => { (clampRefs.current[lead.id] ??= { reason: null, wa: null }).wa = el }}
                        className={isExpanded ? '' : 'truncate'}
                      >
                        {lead.last_wa_summary
                          ? lead.last_wa_summary
                          : <span style={{ color: 'var(--text-disabled)' }}>No interaction yet</span>
                        }
                      </span>
                    </span>

                    {/* Log Call after reveal (Smart Connect OFF desktop) */}
                    {!smartConnectEnabled && revealedMobile && (
                      <button
                        className="btn-secondary shrink-0 ml-auto"
                        style={{ height: '24px', fontSize: '11px', padding: '0 8px' }}
                        onClick={() => handleLogManualCall(lead)}
                        disabled={isCallActive}
                        title="Log the outcome of the call you just made"
                      >
                        <PhoneCall className="h-3 w-3" />
                        Log
                      </button>
                    )}
                  </div>

                  {/* ── Expand / collapse toggle ── */}
                  {hasTruncatable && (
                    <button
                      className="flex items-center gap-0.5 text-xs mt-2"
                      style={{ color: 'var(--text-disabled)' }}
                      onClick={() => toggleExpand(lead.id)}
                    >
                      {isExpanded
                        ? <><ChevronUp className="h-3 w-3" /> Show less</>
                        : <><ChevronDown className="h-3 w-3" /> Show more</>
                      }
                    </button>
                  )}
                </div>
              )
            })
          )}

          {/* Pagination */}
          {!loading && displayedLeads.length > 0 && (
            <div
              className="flex items-center justify-between px-1 pt-3 mt-1"
              style={{ borderTop: '1px solid var(--border-default)' }}
            >
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                Page {page} of {totalPages} · {displayedLeads.length.toLocaleString()} lead{displayedLeads.length !== 1 ? 's' : ''}
              </p>
              <div className="flex gap-1.5">
                <button
                  className="btn-secondary"
                  style={{ height: '28px', fontSize: '12px', padding: '0 10px' }}
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn-secondary"
                  style={{ height: '28px', fontSize: '12px', padding: '0 10px' }}
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Reminders ────────────────────────────────────────────────────── */}
      {tab === 'reminders' && (
        <div className="space-y-3">
          {pendingReminders.length === 0 ? (
            <div className="empty-state">
              <Bell className="h-10 w-10 mb-3" style={{ color: 'var(--text-disabled)' }} />
              <p>No pending reminders</p>
            </div>
          ) : (
            pendingReminders.map((r) => {
              const due = new Date(r.reminder_at)
              const isOverdue = due < new Date()
              return (
                <div key={r.id} className="card flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4 shrink-0"
                        style={{ color: isOverdue ? 'var(--danger-text)' : 'var(--warning-text)' }} />
                      <p className="font-medium truncate" title={r.note || 'No note'} style={{ color: 'var(--text-primary)' }}>
                        {r.note || 'No note'}
                      </p>
                    </div>
                    <p className="text-sm mt-1"
                      style={{ color: isOverdue ? 'var(--danger-text)' : 'var(--text-tertiary)' }}>
                      {isOverdue ? 'Overdue · ' : ''}
                      {due.toLocaleString()}
                      {r.lead_id && (
                        <a href={`/leads/${r.lead_id}`} className="ml-2 hover:underline"
                          style={{ color: 'var(--brand-primary)' }}>
                          Lead #{r.lead_id}
                        </a>
                      )}
                    </p>
                  </div>
                  <button className="btn-secondary shrink-0" onClick={() => handleCompleteReminder(r.id)}>
                    <CheckCircle className="h-3.5 w-3.5" />
                    Done
                  </button>
                </div>
              )
            })
          )}
        </div>
      )}

      </div>{/* end scrollable list */}

      {/* ── Set Reminder Modal ────────────────────────────────────────────── */}
      {reminderLead && (
        <ModalOverlay onClose={() => setReminderLead(null)}>
          <div className="modal-panel" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="flex items-center gap-2.5">
                <Bell className="h-4 w-4" style={{ color: 'var(--brand-600)' }} />
                <div>
                  <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Set Reminder
                  </h2>
                  <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    {reminderLead.customer_name}
                  </p>
                </div>
              </div>
              <button className="btn-icon" onClick={() => setReminderLead(null)} disabled={savingReminder}>
                <X className="h-4 w-4" />
              </button>
            </div>

            {reminderError && (
              <div className="alert-danger mb-1">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{reminderError}</span>
              </div>
            )}

            <div className="space-y-4">
              <div className="form-group">
                <label className="form-label">
                  When <span style={{ color: 'var(--danger-text)' }}>*</span>
                </label>
                <input
                  type="datetime-local"
                  className="input"
                  value={reminderAt}
                  onChange={e => setReminderAt(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Note</label>
                <input
                  type="text"
                  className="input"
                  value={reminderNote}
                  onChange={e => setReminderNote(e.target.value)}
                  placeholder="e.g. Follow up on quote, ask about NCB"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button className="btn-secondary" onClick={() => setReminderLead(null)} disabled={savingReminder}>
                Cancel
              </button>
              <button
                className="btn-primary flex items-center gap-1.5"
                onClick={handleSaveReminder}
                disabled={savingReminder || !reminderAt}
              >
                {savingReminder
                  ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</>
                  : <><Plus className="h-4 w-4" /> Set Reminder</>
                }
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── Enquiry Tracking Panel ───────────────────────────────────────────
           Mobile  (<640 px): full-width bottom sheet, max 88 vh.
           Desktop (≥640 px): fixed right panel, 480 px wide, full height.
      ─────────────────────────────────────────────────────────────────────── */}
      {slideOverLeadId !== null && (
        <>
          {/* Responsive panel styles */}
          <style>{`
            .enquiry-panel {
              position: fixed;
              left: 0;
              right: 0;
              bottom: 0;
              z-index: 50;
              display: flex;
              flex-direction: column;
              max-height: 88vh;
              border-radius: 20px 20px 0 0;
              background: var(--surface-card, var(--surface-page, #1e2230));
              box-shadow: 0 -8px 40px rgba(0,0,0,0.5);
            }
            @media (min-width: 640px) {
              .enquiry-panel {
                left: auto;
                top: 0;
                right: 0;
                bottom: 0;
                width: 480px;
                max-height: 100vh;
                border-radius: 0;
                border-left: 1px solid var(--border-default);
                box-shadow: -4px 0 32px rgba(0,0,0,0.35);
              }
              .enquiry-drag-handle { display: none; }
            }
          `}</style>

          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={() => setSlideOverLeadId(null)}
          />

          <div className="enquiry-panel">
            {/* Drag handle — mobile only */}
            <div className="enquiry-drag-handle flex justify-center pt-3 pb-1">
              <div
                className="rounded-full"
                style={{ width: 44, height: 4, background: 'rgba(255,255,255,0.2)' }}
              />
            </div>

            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 shrink-0"
              style={{ borderBottom: '1px solid var(--border-default)' }}
            >
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                  Enquiry History
                </h2>
                <p className="text-sm truncate" title={slideOverName} style={{ color: 'var(--text-tertiary)', marginTop: 2 }}>
                  {slideOverName}
                </p>
              </div>
              <button
                onClick={() => setSlideOverLeadId(null)}
                aria-label="Close history panel"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 40, minHeight: 40,
                  borderRadius: 10, border: 'none',
                  background: 'rgba(255,255,255,0.08)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  marginLeft: 12,
                  flexShrink: 0,
                }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable timeline body */}
            <div
              className="flex-1 overflow-y-auto"
              style={{ padding: '16px 16px 40px', overscrollBehavior: 'contain' }}
            >
              <EnquiryTrackingCard leadId={slideOverLeadId} />
            </div>
          </div>
        </>
      )}

      {/* ── Call Disposition Modal ────────────────────────────────────────── */}
      <CallDispositionModal
        isOpen={showDisposition}
        callLogId={activeCallLogId}
        leadId={activeLead?.id}
        source={activeCallSource}
        customerName={activeLead?.customer_name ?? ''}
        callStatus={activeCallStatus}
        pollTimedOut={pollTimedOut}
        recordingFound={recordingFound}
        onClose={handleDispositionClose}
        onSaved={handleCallSaved}
        onSubmitOverride={activeCallLogId !== null && activeLead
          ? (callLogId, payload) => submitDisposition(callLogId, activeLead.id, payload)
          : undefined
        }
      />
    </div>
  )
}
